import type { SidebarMenuItem } from '@nebula-layout/layout';

/**
 * El paquete @nebula-layout/layout solo trae LABELS_MAPPING_MENU para ERP (contabilidad, etc.).
 * Las claves de Vault del backend no estÃ¡n mapeadas â†’ getLabel usa formatKey y se ven textos tipo
 * "Sub Modules Vault Docu". AquÃ­ aplicamos espaÃ±ol alineado a vault.routes y al diseÃ±o esperado.
 */

export function normalizeMenuLabelKey(label: string): string {
  return label.trim().replace(/\s+/g, ' ');
}

/** Salida tÃ­pica de formatKey en layout para claves Vault (Title Case). */
const VAULT_GROUP_LABEL_MAP: Record<string, string> = {
  'Sub Modules Vault Home': 'Mi Tablero',
  'Sub Modules Vault Home I': 'Mi Tablero',
  'Sub Modules Vault Docu': 'Centro Documental',
  'Sub Modules Vault System': 'Centro de control',
  'Sub Modules Vault Admin': 'Administrador',
};

/** Etiquetas para hojas cuando formatKey ya generÃ³ "Options Vault X" (sin depender solo de la ruta). */
const VAULT_OPTIONS_LABEL_MAP: Record<string, string> = {
  'Options Vault Ingest': 'Carga de Documentos',
  'Options Vault Preservation': 'Preservación Digital',
  'Options Vault Retention': 'Retención Documental',
  'Options Vault Legal Hold': 'Custodia Legal',
  'Options Vault Repository Admin': 'Bóveda Digital',
  'Options Vault Reports': 'Reportes Generales',
  'Options Vault Cloud': 'Respaldo Externo',
  'Options Vault Notifications': 'Notificaciones',
  'Options Vault Audit': 'Auditoría',
  'Options Vault Settings': 'Configuración',
  'Options Vault Preservation Messages': 'Mensajes Informativos',
  'Options Vault Admin Dashboard': 'Admin Dashboard',
  'Options Vault Admin Tenants': 'Gestión de Empresas',
  'Options Vault Admin User': 'Gestión de Usuarios',
  'Options Vault Admin Users': 'Gestión de Usuarios',
  'Options Vault Admin Module Permissions': 'Permisos',
};

function fuzzyVaultGroupLabel(label: string): string | undefined {
  const n = normalizeMenuLabelKey(label).toLowerCase();
  if (n.includes('sub modules vault home')) return 'Mi Tablero';
  if (n.includes('sub modules vault docu')) return 'Centro Documental';
  if (n.includes('sub modules vault system')) return 'Centro de control';
  if (n.includes('sub modules vault admin')) return 'Administrador';
  return undefined;
}

/**
 * Resuelve etiqueta en espaÃ±ol a partir de la ruta bajo /layout/vault/â€¦
 * (rutas reales: /layout/vault/audit, /layout/vault/vault/projects, etc.)
 */
export function vaultDisplayLabelFromRoute(route: string | null | undefined): string | null {
  if (!route) return null;
  const clean = route
    .toLowerCase()
    .replace(/\/+/g, '/')
    .split('?')[0]
    .split('#')[0];

  let rest = clean.match(/\/layout\/vault\/(.+)$/)?.[1] ?? null;
  if (rest == null) {
    if (clean.startsWith('/vault/')) rest = clean.slice('/vault/'.length);
    else if (clean.startsWith('vault/')) rest = clean.slice('vault/'.length);
  }
  rest = (rest ?? '').replace(/\/$/, '');
  if (!rest) return null;

  const parts = rest.split('/').filter(Boolean);
  if (parts.length === 0) return null;

  const head = parts[0];

  // Ingesta: .../vault/vault/... (mÃ³dulo hijo `vault` en vault.routes)
  if (head === 'vault') {
    return 'Carga de Documentos';
  }

  if (head === 'dashboard') return 'Inicio';
  if (head === 'preservation') return 'Preservación Digital';
  if (head === 'retention') return 'Retención Documental';
  if (head === 'legal-hold') return 'Custodia Legal';
  if (head === 'repository-admin') return 'Bóveda Digital';
  if (head === 'reports') return 'Reportes Generales';
  if (head === 'cloud') return 'Respaldo Externo';
  if (head === 'notifications') return 'Notificaciones';
  if (head === 'audit') return 'Auditoría';
  if (head === 'settings') {
    if (parts[1] === 'profile') return 'Perfil';
    return 'Configuración';
  }
  if (head === 'preservation-messages') return 'Mensajes Informativos';

  if (head === 'admin') {
    const sub = parts[1];
    if (sub === 'dashboard') return 'Admin Dashboard';
    if (sub === 'tenants') return 'Gestión de Empresas';
    if (sub === 'user' || sub === 'users') return 'Gestión de Usuarios';
    if (sub === 'module-permissions') return 'Permisos';
    if (sub === 'servers') return 'Configuración Servidores';
    return 'Administrador';
  }

  return null;
}

function fuzzyVaultLeafLabel(label: string): string | undefined {
  const n = normalizeMenuLabelKey(label).toLowerCase();
  if (n.includes('ingest') || /options vault ingest/.test(n)) return 'Carga de Documentos';
  if (/options vault preservation messages/.test(n)) return 'Mensajes Informativos';
  if (/options vault preservation/.test(n)) return 'Preservación Digital';
  if (/options vault retention|retencion/.test(n)) return 'Retención Documental';
  if (/legal hold|custodia/.test(n)) return 'Custodia Legal';
  if (/repositorio|repository|boveda/.test(n)) return 'Bóveda Digital';
  if (/options vault reports/.test(n)) return 'Reportes Generales';
  if (/options vault cloud|respaldo externo/.test(n)) return 'Respaldo Externo';
  if (/options vault notifications/.test(n)) return 'Notificaciones';
  if (/options vault audit|auditoria/.test(n)) return 'Auditoría';
  if (/options vault settings|configuracion/.test(n)) return 'Configuración';
  if (/options vault admin tenants|gestion de empresas/.test(n)) return 'Gestión de Empresas';
  if (/options vault admin users?/.test(n)) return 'Gestión de Usuarios';
  if (/options vault admin module permissions|permisos/.test(n)) return 'Permisos';
  if (/options vault admin dashboard/.test(n)) return 'Admin Dashboard';
  // Claves truncadas en UI o variantes del backend
  if (/options vault admin/.test(n)) {
    if (n.includes('tenant')) return 'Gestión de Empresas';
    if (n.includes('user')) return 'Gestión de Usuarios';
    if (n.includes('permission') || n.includes('permis')) return 'Permisos';
    if (n.includes('dashboard')) return 'Admin Dashboard';
  }
  return undefined;
}

function mapMenuItem(item: SidebarMenuItem): SidebarMenuItem {
  const children = item.children?.length ? item.children.map(mapMenuItem) : undefined;
  const base: SidebarMenuItem = children ? { ...item, children } : { ...item };

  const hasKids = children && children.length > 0;
  if (hasKids) {
    const key = normalizeMenuLabelKey(base.label);
    const label = VAULT_GROUP_LABEL_MAP[key] ?? fuzzyVaultGroupLabel(base.label) ?? base.label;
    return { ...base, label };
  }

  const routeLbl = vaultDisplayLabelFromRoute(base.route);
  const key = normalizeMenuLabelKey(base.label);
  const label =
    routeLbl ??
    VAULT_GROUP_LABEL_MAP[key] ??
    VAULT_OPTIONS_LABEL_MAP[key] ??
    fuzzyVaultLeafLabel(base.label) ??
    base.label;
  return { ...base, label };
}

export function applyVaultMenuLabels(menu: SidebarMenuItem[]): SidebarMenuItem[] {
  return menu.map(mapMenuItem);
}
